
import { Account, Product, Geography, SalesTransaction, SalesRepMapping } from "./types";

/**
 * Interface for validation errors
 */
export interface ValidationError {
  entityType: string;
  entityId: string;
  errorType: string;
  message: string;
}

/**
 * Validates account data for common issues
 */
export function validateAccounts(accounts: Account[]): ValidationError[] {
  const errors: ValidationError[] = [];
  
  // Check for duplicate IDs
  const accountIds = accounts.map(a => a.id);
  const duplicateIds = accountIds.filter((id, index) => accountIds.indexOf(id) !== index);
  
  duplicateIds.forEach(id => {
    errors.push({
      entityType: 'Account',
      entityId: id,
      errorType: 'Duplicate',
      message: `Account ID ${id} appears multiple times in the dataset`
    });
  });
  
  // Check for required fields
  accounts.forEach(account => {
    if (!account.name) {
      errors.push({
        entityType: 'Account',
        entityId: account.id,
        errorType: 'MissingData',
        message: `Account ${account.id} is missing a name`
      });
    }
    
    if (!account.region) {
      errors.push({
        entityType: 'Account',
        entityId: account.id,
        errorType: 'MissingData',
        message: `Account ${account.name || account.id} is missing a region`
      });
    }
    
    if (!account.country) {
      errors.push({
        entityType: 'Account',
        entityId: account.id,
        errorType: 'MissingData',
        message: `Account ${account.name || account.id} is missing a country`
      });
    }
  });
  
  return errors;
}

/**
 * Validates product data for common issues
 */
export function validateProducts(products: Product[]): ValidationError[] {
  const errors: ValidationError[] = [];
  
  // Check for duplicate IDs
  const productIds = products.map(p => p.id);
  const duplicateIds = productIds.filter((id, index) => productIds.indexOf(id) !== index);
  
  duplicateIds.forEach(id => {
    errors.push({
      entityType: 'Product',
      entityId: id,
      errorType: 'Duplicate',
      message: `Product ID ${id} appears multiple times in the dataset`
    });
  });
  
  // Check for required fields
  products.forEach(product => {
    if (!product.name) {
      errors.push({
        entityType: 'Product',
        entityId: product.id,
        errorType: 'MissingData',
        message: `Product ${product.id} is missing a name`
      });
    }
    
    if (!product.productLine) {
      errors.push({
        entityType: 'Product',
        entityId: product.id,
        errorType: 'MissingData',
        message: `Product ${product.name || product.id} is missing a product line`
      });
    }
  });
  
  return errors;
}

/**
 * Validates sales transactions for common issues and orphaned references
 */
export function validateTransactions(
  transactions: SalesTransaction[], 
  accounts: Account[],
  products: Product[]
): ValidationError[] {
  const errors: ValidationError[] = [];
  const accountIds = accounts.map(a => a.id);
  const productIds = products.map(p => p.id);
  
  // Check for orphaned references
  transactions.forEach(transaction => {
    const transactionId = transaction.invoiceId;
    
    if (!accountIds.includes(transaction.accountId)) {
      errors.push({
        entityType: 'Transaction',
        entityId: transactionId,
        errorType: 'OrphanedReference',
        message: `Transaction ${transactionId} references unknown account ID ${transaction.accountId}`
      });
    }
    
    if (!productIds.includes(transaction.productId)) {
      errors.push({
        entityType: 'Transaction',
        entityId: transactionId,
        errorType: 'OrphanedReference',
        message: `Transaction ${transactionId} references unknown product ID ${transaction.productId}`
      });
    }
    
    if (transaction.revenue <= 0) {
      errors.push({
        entityType: 'Transaction',
        entityId: transactionId,
        errorType: 'InvalidValue',
        message: `Transaction ${transactionId} has invalid revenue value ${transaction.revenue}`
      });
    }
  });
  
  return errors;
}

/**
 * Run all data quality validations
 */
export function validateDataQuality(
  accounts: Account[],
  products: Product[],
  transactions: SalesTransaction[]
): ValidationError[] {
  const accountErrors = validateAccounts(accounts);
  const productErrors = validateProducts(products);
  const transactionErrors = validateTransactions(transactions, accounts, products);
  
  return [...accountErrors, ...productErrors, ...transactionErrors];
}
